import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { DateTime } from 'luxon';
import { BsSearch } from 'react-icons/bs';
import { FiAlertCircle, FiFileText } from 'react-icons/fi';
import { Model, ModelService } from '../services';
import { usePrescriptionStore } from '../../../stores/prescriptionStore';
import {
  FILTER_TYPES,
  GroupedModels,
  computeFilteredModels,
  computeGroupedModels,
  useModelStore,
} from '../../../stores/modelStore';
import { PrescriptionModelCard } from './PrescriptionModelCard';
import { useModelFilterPills } from './modelFilterPills';
import { ApplyModelDrawer } from './ApplyModelDrawer';
import { EditModelDrawer } from './EditModelDrawer';
import { Flex as ChakraFlex } from '@chakra-ui/react';
import { Box } from '../../../components/UI-CHAKRA/Box';
import { Flex } from '../../../components/UI-CHAKRA/Flex';
import { Text } from '../../../components/UI-CHAKRA/Text';
import { Spinner } from '../../../components/UI-CHAKRA/Spinner';
import { Input } from '../../../components/UI-CHAKRA/Input/Input';
import { InputGroup } from '../../../components/UI-CHAKRA/Input/InputGroup';
import { InputRightElement } from '../../../components/UI-CHAKRA/Input/InputRightElement';
import { Tracker } from '../../../shared/tracker/Tracker';
import { amplitudeModule } from '../../../shared/amplitude';
import { TrackerEventService } from '../../prescription/trackerEventService';
import { FeedbackContext } from '../../../providers/FeedbackProvider';
import { PrescriptionContext } from '../../../providers/PrescriptionProvider';
import { useIsMobile } from '../../../helpers/useIsMobile';
import { useDragToScroll } from '../../../helpers/useDragToScroll';
import { logger } from '../../../helpers/logger';
import { extractPatientAge } from '../../../helpers/patientAge';
import { toast } from '../../../helpers/toast';
import { useDebouncedValue, SEARCH_DEBOUNCE_MS } from '../../../helpers/useDebouncedValue';

export function PrescriptionModels() {
  const { models, sponsoredModels, filter, setFilter, searchFilter, setSearchFilter, fetchModels } = useModelStore();
  const setFeedback = useModelStore((store) => store.setFeedback);
  const loadMoreModels = useModelStore((store) => store.loadMoreModels);
  const loadingMoreModels = useModelStore((store) => store.loadingMoreModels);
  const modelsError = useModelStore((store) => store.modelsError);
  const hasMoreModels = useModelStore((store) => store.models.length < store.modelsTotal);
  const modelsRequestId = useModelStore((store) => store.modelsRequestId);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // `groupedModels` calculado via `useMemo` em vez do seletor `store.groupedModels()`: o método
  // do store retorna um objeto novo a cada chamada e dispara re-render mesmo sem mudança nos
  // inputs. `computeFilteredModels` e `computeGroupedModels` são puras.
  const filteredModels = useMemo(
    () => computeFilteredModels(models, sponsoredModels, searchFilter),
    [models, sponsoredModels, searchFilter]
  );
  const groupedModels: GroupedModels = useMemo(() => computeGroupedModels(filteredModels), [filteredModels]);

  // As pills ignoram a busca de propósito: quem as define é a lista carregada sem termo.
  const pillOptions = useModelFilterPills(models, sponsoredModels);

  const { prescription } = usePrescriptionStore();
  const { askForConfirmation } = useContext(FeedbackContext);
  const { loadingModel } = useContext(PrescriptionContext);
  const { prescriberConfiguration } = usePrescriptionStore();
  const prescriber = usePrescriptionStore().prescriber();

  /**
   * "A listagem ainda não respondeu" — cobre também o intervalo anterior à requisição.
   *
   * `loadingModel` só fica true quando o provider chega ao fetch, bem depois da tela montar.
   * Nesse intervalo a lista está vazia porque nada foi pedido ainda, e olhar só para ele
   * renderizaria "nenhum modelo" e a pill "Todos" antes de haver resposta. `modelsRequestId`
   * passa de zero na primeira requisição disparada, o que fecha a janela.
   */
  const loadingModelsList = loadingModel || modelsRequestId === 0;

  const isMobile = useIsMobile();
  const pillsDrag = useDragToScroll<HTMLDivElement>();
  const eventTriggeredRef = useRef(false);
  const [activeModel, setActiveModel] = useState<Model | null>(null);
  const [editingModel, setEditingModel] = useState<Model | null>(null);

  const prescriptionItemsAmount = usePrescriptionStore((store) => store.prescriptionItemsAmount());
  const prescriptionObservation = usePrescriptionStore((store) => store.prescriptionObservation());

  /**
   * Tracking de visualização/clique nos modelos "Sugestão" (hoje, só patrocinados).
   *
   * Antes existia `tipoModelo: 1` para destacados-não-patrocinados, mas o campo `highlighted`
   * foi descontinuado no backend. Mantemos só `tipoModelo: 2`.
   */
  const trackSponsoredModelView = useCallback(
    async (extraProps: { idPrescricaoModelo: string[] | string; event: string }) => {
      try {
        if (!prescription?.idPrescricao) return;

        await TrackerEventService.track(prescription.idPrescricao, extraProps.event, {
          idMedico: prescriber?.idPrescritor,
          idPrescricao: prescription.idPrescricao,
          idParceiro: prescriberConfiguration?.idParceiro,
          codigoValidacao: prescription.CodigoValidacao,
          tipoModelo: 2,
          idPrescricaoModelo: extraProps.idPrescricaoModelo,
          Timestamp: DateTime.now(),
          isMobile,
        });
      } catch (error) {
        // Tracking não deve quebrar a UI, mas logamos para detectar regressões silenciosas.
        logger.error('trackSponsoredModelView: tracker call failed', error);
      }
    },
    [
      prescriber?.idPrescritor,
      prescriberConfiguration?.idParceiro,
      prescription?.idPrescricao,
      prescription?.CodigoValidacao,
      isMobile,
    ]
  );

  const onSelectModel = useCallback(
    async (model: Model) => {
      if (!prescription) return;

      amplitudeModule.searchResultClicked({
        partner_id: prescriberConfiguration?.idParceiro ?? 0,
        partner_name: prescriberConfiguration?.Nome ?? '',
        establishment_name: prescription.Estabelecimento?.Nome ?? '',
        validation_code: prescription.CodigoValidacao ?? '',
        prescription_id: prescription.idPrescricao ?? 0,
        screen_name: 'modelos',
        search_type: 'Modelo',
        search_term: searchFilter,
        protocol_id: model.id,
        protocol_name: model.name ?? '',
      });

      if (model.sponsored) {
        trackSponsoredModelView({
          event: 'backoffice_prescription_models_view_clicou',
          idPrescricaoModelo: model.id,
        });
      }

      setActiveModel(model);
    },
    [prescription, prescriberConfiguration, searchFilter, trackSponsoredModelView]
  );

  /**
   * Impressão dos patrocinados.
   *
   * Conta o que está **renderizado** na pill "Sugestão" (`groupedModels.highlighted`), e não a
   * lista `sponsoredModels`. As duas divergem: dependendo das feature flags os patrocinados
   * chegam pela listagem ou pelo endpoint dedicado, e a listagem ainda é paginada. Medir o que
   * foi para a tela mantém o evento correto nas duas configurações e imune a paginação.
   */
  useEffect(() => {
    if (eventTriggeredRef.current) return;

    const rendered = groupedModels.highlighted;
    if (rendered.length === 0) return;

    trackSponsoredModelView({
      event: 'backoffice_prescription_models_view',
      idPrescricaoModelo: rendered.map((model) => model.id),
    });

    eventTriggeredRef.current = true;
  }, [trackSponsoredModelView, groupedModels]);

  const searchTerm = searchFilter.trim();
  const debouncedSearch = useDebouncedValue(searchTerm, SEARCH_DEBOUNCE_MS);

  /**
   * Busca a lista quando o termo muda e, na primeira vez, quando o `partnerId` fica
   * disponível.
   *
   * `fetchModels` aborta sem carregar nada enquanto o `prescriberConfiguration` não trouxer
   * `idParceiro` — e sem marcar erro, porque é um estado transitório. Só que o termo de busca
   * também não muda nesse intervalo, então sem o `modelsRequestId` no guard a lista ficaria
   * vazia para sempre, exibida como "nenhum modelo". O contador só passa de zero quando uma
   * busca de fato começou, o que o torna o sinal de "ainda não carregou".
   */
  useEffect(() => {
    if (!prescription) return;
    const { modelsRequestId, modelsName } = useModelStore.getState();
    const neverLoaded = modelsRequestId === 0;
    if (!neverLoaded && (debouncedSearch || undefined) === modelsName) return;
    fetchModels(
      prescription.Prescritor.idPrescritor,
      prescription.idPrescricao,
      extractPatientAge(prescription.Paciente?.Idade),
      debouncedSearch || undefined,
    ).catch((error) => logger.error('Error searching models:', error));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, prescription?.idPrescricao, prescriberConfiguration?.idParceiro]);

  const refreshModels = useCallback(async () => {
    if (!prescription) return;
    try {
      await fetchModels(
        prescription.Prescritor.idPrescritor,
        prescription.idPrescricao,
        extractPatientAge(prescription.Paciente?.Idade),
        searchTerm || undefined,
        { force: true },
      );
    } catch (error) {
      logger.error('Error fetching models:', error);
    }
  }, [prescription, fetchModels, searchTerm]);

  const onDeleteModel = useCallback(
    async (model: Model) => {
      const confirmation = await askForConfirmation({
        text: 'O modelo selecionado será excluído permanentemente.',
        confirmText: 'Deletar modelo',
        cancelText: 'Cancelar',
        title: 'Deletar modelo salvo',
      });

      if (!confirmation) return;

      const doctorId = prescription?.Prescritor?.idPrescritor;
      const partnerId = prescriberConfiguration?.idParceiro;
      const prescriptionId = prescription?.idPrescricao;
      if (!doctorId || !partnerId || !prescriptionId) {
        toast.error('Não foi possível deletar o modelo, tente novamente.');
        return;
      }

      try {
        await ModelService.deleteModel(model.id, doctorId, partnerId, prescriptionId);
        await refreshModels();
        Tracker.trackEvent({
          event: 'Model Deleted',
          parameters: { deletedModel: model.name },
        });
        setFeedback({ type: 'removed', modelName: model.name });
      } catch (e) {
        logger.error(e);
        toast.error('Não foi possível deletar o modelo, tente novamente.');
      }
    },
    [askForConfirmation, refreshModels, setFeedback, prescription, prescriberConfiguration]
  );

  const onEditModel = useCallback((model: Model) => {
    setEditingModel(model);
  }, []);

  const visibleModels = useMemo(() => {
    return groupedModels[filter as keyof typeof groupedModels] ?? [];
  }, [groupedModels, filter]);

  // O filtro vive no store e sobrevive a mudanças na lista. Se a pill selecionada deixa de
  // existir, cair para "Todos" evita a lista vazia sem nenhuma pill marcada.
  useEffect(() => {
    if (filter === FILTER_TYPES.ALL) return;
    if (pillOptions.some((option) => option.filter === filter)) return;
    setFilter(FILTER_TYPES.ALL);
  }, [pillOptions, filter, setFilter]);

  /**
   * Paginação por sentinela — só depois que a primeira página está na tela.
   *
   * Sem o `loadingModelsList` a sentinela já está visível sob o skeleton assim que
   * `modelsTotal` chega, e a página 2 é buscada antes de a lista ter sido renderizada: o
   * spinner de "carregando mais" aparece junto do skeleton, dizendo duas coisas opostas.
   *
   * `modelsRequestId`/`modelsError` entram nas dependências de propósito: `loadMoreModels`
   * ignora o pedido em silêncio enquanto a primeira página está em voo, e quem re-dispara é a
   * recriação deste observer. Só `visibleModels.length` não basta — uma primeira página que
   * falha não altera a lista, e a paginação ficaria parada até o usuário rolar o sentinel para
   * fora e de volta.
   */
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !prescription || !hasMoreModels || loadingModelsList) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadMoreModels(prescription.Prescritor.idPrescritor, prescription.idPrescricao);
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [
    prescription,
    hasMoreModels,
    loadMoreModels,
    loadingModelsList,
    visibleModels.length,
    modelsRequestId,
    modelsError,
  ]);

  const isVisible = prescriptionItemsAmount === 0 && !prescriptionObservation.trim();

  return (
    <>
      <AnimatePresence initial={false}>
        {isVisible && (
          <motion.div
            key={'prescription-models-list'}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            style={{ overflow: 'hidden' }}
          >
            <Box data-testid={'prescription-models-list'} mt={'20px'}>
              <Flex
                w={'100%'}
                gap={'16px'}
                alignItems={{ base: 'stretch', md: 'center' }}
                justifyContent={'space-between'}
                flexDirection={{ base: 'column-reverse', md: 'row' }}
                flexWrap={'wrap'}
                mb={'16px'}
              >
                {!loadingModelsList && (
                  <ChakraFlex
                    ref={pillsDrag.ref}
                    {...pillsDrag.handlers}
                    gap={'8px'}
                    flexWrap={'nowrap'}
                    overflowX={'auto'}
                    minW={0}
                    w={{ base: '100%', md: 'auto' }}
                    flex={{ base: 'unset', md: '1 1 0' }}
                    cursor={'grab'}
                    _active={{ cursor: 'grabbing' }}
                    css={{
                      '::-webkit-scrollbar': { display: 'none' },
                      scrollbarWidth: 'none',
                      WebkitOverflowScrolling: 'touch',
                      userSelect: 'none',
                      touchAction: 'pan-x',
                    }}
                  >
                    {pillOptions.map((option) => {
                      const isActive = filter === option.filter;
                      return (
                        <Box
                          key={option.filter}
                          as={'button'}
                          onClick={() => setFilter(option.filter)}
                          px={'16px'}
                          h={'40px'}
                          flexShrink={0}
                          whiteSpace={'nowrap'}
                          borderRadius={'20px'}
                          border={'1px solid'}
                          borderColor={isActive ? 'primary.main' : '#DEE2E6'}
                          bg={isActive ? 'primary.main' : 'white'}
                          color={isActive ? 'white' : '#1E252B'}
                          fontSize={'14px'}
                          fontWeight={500}
                          cursor={'pointer'}
                          transition={'all 0.15s ease'}
                          _hover={{ bg: isActive ? 'primary.main' : 'gray.lightest' }}
                          data-testid={`model-filter-pill-${option.filter}`}
                        >
                          {option.label}
                        </Box>
                      );
                    })}
                  </ChakraFlex>
                )}

                {/*
                  Enquanto a listagem não responde não há linha de pills, e o `space-between`
                  do container puxaria a busca sozinha para a esquerda — ela saltaria de lado
                  quando as pills entrassem. O `auto` à esquerda fixa a busca na direita nos
                  dois estados.
                */}
                <Box w={{ base: '100%', md: '312px' }} ml={{ base: 0, md: 'auto' }}>
                  <InputGroup h={'40px'}>
                    <Input
                      value={searchFilter}
                      onChange={(e) => setSearchFilter(e.target.value)}
                      placeholder={'Busque por nome do modelo'}
                      h={'40px'}
                      fontSize={'14px'}
                      borderRadius={'10px'}
                      borderColor={'#CBBFE6'}
                      data-testid={'model-search-input'}
                    />
                    <InputRightElement h={'40px'} pointerEvents={'none'}>
                      <BsSearch size={16} color={'#868E96'} />
                    </InputRightElement>
                  </InputGroup>
                </Box>
              </Flex>

              <Box
                bg={'white'}
                border={'1px dashed'}
                borderColor={'#E9ECEE'}
                borderRadius={'10px'}
                pt={'12px'}
                px={'16px'}
                pb={'16px'}
                display={'flex'}
                flexDirection={'column'}
                gap={'16px'}
                w={'100%'}
                data-testid={'prescription-models-panel'}
              >
                <Flex direction={'column'} gap={'4px'} data-testid={'prescription-empty-message'}>
                  <Text fontSize={'14px'} fontWeight={700} color={'#343A40'} lineHeight={'1.2'}>
                    Sua prescrição está vazia
                  </Text>
                  <Text fontSize={'14px'} color={'#766F7C'} lineHeight={'1.2'}>
                    Para começar sua prescrição com um modelo, selecione-o abaixo.
                  </Text>
                </Flex>

                {loadingModelsList ? (
                  <ModelsSkeletonGrid />
                ) : modelsError ? (
                  <ModelsErrorState onRetry={refreshModels} />
                ) : visibleModels.length === 0 ? (
                  <ModelsEmptyState />
                ) : (
                  <Box
                    display={'grid'}
                    gridTemplateColumns={{
                      base: 'repeat(1, 1fr)',
                      sm: 'repeat(2, 1fr)',
                      lg: 'repeat(4, 1fr)',
                    }}
                    gap={'24px'}
                  >
                    {visibleModels.map((model) => (
                      <PrescriptionModelCard
                        key={model.id}
                        model={model}
                        itemCount={model.itemsCount}
                        onSelectModel={onSelectModel}
                        onEditModel={onEditModel}
                        onDeleteModel={onDeleteModel}
                      />
                    ))}
                  </Box>
                )}
              </Box>

              <ChakraFlex ref={sentinelRef} h={'1px'} w={'100%'} />

              {loadingMoreModels && !loadingModelsList && (
                <Flex
                  justifyContent={'center'}
                  py={'24px'}
                  w={'100%'}
                  data-testid={'prescription-models-loading-more'}
                >
                  <Spinner thickness={'2px'} color={'primary.main'} size={'md'} />
                </Flex>
              )}
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
      <ApplyModelDrawer
        isOpen={Boolean(activeModel)}
        model={activeModel}
        entryPoint="tela_principal"
        onClose={() => setActiveModel(null)}
      />
      <EditModelDrawer isOpen={Boolean(editingModel)} model={editingModel} onClose={() => setEditingModel(null)} />
    </>
  );
}

function ModelsSkeletonGrid() {
  return (
    <Box
      data-testid={'prescription-models-skeleton'}
      display={'grid'}
      gridTemplateColumns={{
        base: 'repeat(1, 1fr)',
        sm: 'repeat(2, 1fr)',
        lg: 'repeat(4, 1fr)',
      }}
      gap={'24px'}
    >
      {Array.from({ length: 8 }).map((_, index) => (
        <Box
          key={index}
          h={'84px'}
          w={'100%'}
          bg={'white'}
          border={'1px solid #DEE2E6'}
          borderRadius={'8px'}
          p={'8px'}
          display={'flex'}
          flexDirection={'column'}
          gap={'12px'}
          css={{
            '@keyframes modelSkeletonPulse': {
              '0%': { opacity: 0.6 },
              '50%': { opacity: 1 },
              '100%': { opacity: 0.6 },
            },
            animation: 'modelSkeletonPulse 1.4s ease-in-out infinite',
          }}
        >
          <Flex gap={'8px'} alignItems={'center'}>
            <Box h={'24px'} w={'92px'} bg={'#F1F3F5'} borderRadius={'15px'} />
          </Flex>
          <Box h={'14px'} w={'80%'} bg={'#F1F3F5'} borderRadius={'4px'} />
        </Box>
      ))}
    </Box>
  );
}

function ModelsErrorState(props: { onRetry: () => void }) {
  return (
    <Flex
      data-testid={'models-error-state'}
      direction={'column'}
      alignItems={'center'}
      justifyContent={'center'}
      gap={'8px'}
      py={'56px'}
      w={'100%'}
    >
      <Flex
        alignItems={'center'}
        justifyContent={'center'}
        w={'56px'}
        h={'56px'}
        borderRadius={'50%'}
        bg={'#FFF4F4'}
        color={'#FF6161'}
        mb={'8px'}
      >
        <FiAlertCircle size={24} />
      </Flex>
      <Text fontSize={'18px'} fontWeight={600} color={'#1E252B'} lineHeight={'1.3'}>
        Não foi possível carregar os modelos
      </Text>
      <Text fontSize={'14px'} color={'#868E96'} textAlign={'center'} maxW={'370px'} lineHeight={'1.4'}>
        Verifique sua conexão e tente novamente.
      </Text>
      <Box
        as={'button'}
        onClick={props.onRetry}
        mt={'8px'}
        bg={'transparent'}
        border={'none'}
        cursor={'pointer'}
        p={'8px 12px'}
        data-testid={'models-error-retry'}
      >
        <Text fontSize={'14px'} fontWeight={500} color={'primary.main'} textDecoration={'underline'}>
          Tentar novamente
        </Text>
      </Box>
    </Flex>
  );
}

function ModelsEmptyState() {
  return (
    <Flex
      data-testid={'models-empty-state'}
      direction={'column'}
      alignItems={'center'}
      justifyContent={'center'}
      gap={'8px'}
      py={'56px'}
      w={'100%'}
    >
      <Flex
        alignItems={'center'}
        justifyContent={'center'}
        w={'56px'}
        h={'56px'}
        borderRadius={'50%'}
        bg={'gray.lightest'}
        color={'primary.main'}
        mb={'8px'}
      >
        <FiFileText size={24} />
      </Flex>
      <Text fontSize={'18px'} fontWeight={600} color={'#1E252B'} lineHeight={'1.3'}>
        Nenhum modelo encontrado
      </Text>
      <Text fontSize={'14px'} color={'#868E96'} textAlign={'center'} maxW={'370px'} lineHeight={'1.4'}>
        Não há modelos disponíveis no momento. Tente ajustar seus filtros ou crie um novo modelo.
      </Text>
    </Flex>
  );
}
